import { Component, inject, input, output, signal, OnInit, effect } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { of, Subject, forkJoin } from 'rxjs';
import {
  ReferralDirectoryRepositoryService,
  ReferralDoctor,
  ReferralSpecialty,
  ReferralFacility
} from '@xmedical/core';

@Component({
  selector: 'xm-doctor-selector',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule
],
  templateUrl: './doctor-selector.component.html',
  styleUrl: './doctor-selector.component.scss'
})
export class DoctorSelectorComponent implements OnInit {
  private directoryRepo = inject(ReferralDirectoryRepositoryService);

  // Inputs
  selectedDoctor = input<ReferralDoctor | null>(null);
  title = input('Select Doctor');
  required = input(false);

  // Outputs
  selectedDoctorChange = output<ReferralDoctor | null>();

  // Internal state for the selected doctor (synced with input)
  private _selectedDoctor = signal<ReferralDoctor | null>(null);

  constructor() {
    // Sync input to internal state
    effect(() => {
      const doctor = this.selectedDoctor();
      this._selectedDoctor.set(doctor);
    });
  }

  // Filter options
  specialties = signal<ReferralSpecialty[]>([]);
  facilities = signal<ReferralFacility[]>([]);
  selectedSpecialtyId = signal<number | null>(null);
  selectedFacilityId = signal<number | null>(null);

  // Search state
  searchQuery = signal('');
  searchResults = signal<ReferralDoctor[]>([]);
  isSearching = signal(false);
  showResults = signal(false);
  isLoadingFilters = signal(false);

  private searchSubject = new Subject<string>();

  ngOnInit(): void {
    // Load filter options
    this.loadFilterOptions();

    // Setup search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length >= 2) {
          this.isSearching.set(true);
          return this.performSearch(query);
        }
        return of([]);
      })
    ).subscribe(results => {
      this.searchResults.set(results);
      this.isSearching.set(false);
      this.showResults.set(results.length > 0 || this.searchQuery().length >= 2);
    });
  }

  private loadFilterOptions(): void {
    this.isLoadingFilters.set(true);
    forkJoin({
      specialties: this.directoryRepo.getSpecialties(),
      facilities: this.directoryRepo.getFacilities()
    }).subscribe({
      next: ({specialties, facilities}) => {
        this.specialties.set(specialties.filter(s => s.active));
        this.facilities.set(facilities.filter(f => f.active));
        this.isLoadingFilters.set(false);
      },
      error: () => {
        this.isLoadingFilters.set(false);
      }
    });
  }

  private performSearch(query: string) {
    // If filters are applied, get doctors by filter then filter by search term
    const specialtyId = this.selectedSpecialtyId();
    const facilityId = this.selectedFacilityId();

    if (specialtyId && facilityId) {
      // Both filters - get by specialty and filter further
      return this.directoryRepo.getDoctorsBySpecialty(specialtyId).pipe(
        switchMap(doctors => {
          const filtered = doctors.filter(d =>
            d.facilityId === facilityId &&
            d.name.toLowerCase().includes(query.toLowerCase())
          );
          return of(filtered);
        }),
        catchError(() => of([]))
      );
    } else if (specialtyId) {
      // Only specialty filter
      return this.directoryRepo.getDoctorsBySpecialty(specialtyId).pipe(
        switchMap(doctors => {
          const filtered = doctors.filter(d =>
            d.name.toLowerCase().includes(query.toLowerCase())
          );
          return of(filtered);
        }),
        catchError(() => of([]))
      );
    } else if (facilityId) {
      // Only facility filter
      return this.directoryRepo.getDoctorsByFacility(facilityId).pipe(
        switchMap(doctors => {
          const filtered = doctors.filter(d =>
            d.name.toLowerCase().includes(query.toLowerCase())
          );
          return of(filtered);
        }),
        catchError(() => of([]))
      );
    } else {
      // No filters - use direct search
      return this.directoryRepo.searchDoctors(query).pipe(
        catchError(() => of([]))
      );
    }
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery.set(value);
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.searchResults.set([]);
    this.showResults.set(false);
  }

  onSpecialtyChange(specialtyId: number | null): void {
    this.selectedSpecialtyId.set(specialtyId);
    // Re-trigger search if there's a query
    if (this.searchQuery().length >= 2) {
      this.searchSubject.next(this.searchQuery());
    }
  }

  onFacilityChange(facilityId: number | null): void {
    this.selectedFacilityId.set(facilityId);
    // Re-trigger search if there's a query
    if (this.searchQuery().length >= 2) {
      this.searchSubject.next(this.searchQuery());
    }
  }

  clearFilters(): void {
    this.selectedSpecialtyId.set(null);
    this.selectedFacilityId.set(null);
    // Re-trigger search if there's a query
    if (this.searchQuery().length >= 2) {
      this.searchSubject.next(this.searchQuery());
    }
  }

  selectDoctor(doctor: ReferralDoctor): void {
    this._selectedDoctor.set(doctor);
    this.selectedDoctorChange.emit(doctor);
    this.clearSearch();
  }

  clearSelection(): void {
    this._selectedDoctor.set(null);
    this.selectedDoctorChange.emit(null);
  }

  // Getter for template access to internal state
  get currentDoctor(): ReferralDoctor | null {
    return this._selectedDoctor();
  }

  getDoctorTypeLabel(doctor: ReferralDoctor): string {
    if (doctor.isSystemUser) {
      return 'System User';
    } else if (doctor.isPortalUser) {
      return 'Portal User';
    } else {
      return 'External';
    }
  }

  getDoctorTypeColor(doctor: ReferralDoctor): string {
    if (doctor.isSystemUser) {
      return 'primary';
    } else if (doctor.isPortalUser) {
      return 'accent';
    } else {
      return 'warn';
    }
  }
}
